import React from 'react';
import {faInbox, faSearch} from '@netx/core-icons';
import {Button} from '@mui/material';
import {EmptyStateFallbackMessage} from '@netx/core-components/EmptyStateFallbackMessage';
import {SearchFilter} from '@netx/core-components/SearchFilter';
import {styled} from '@netx/core-components/Theme';
import {appRouter} from '@netx/core-next/instance/router';
import {SavedCartsProvider, SharedResourceMode} from '../../contexts';
import {
	CartsSavedLabel,
	CartsSavedMessage,
	CartsSavedTitle,
} from '../../locales';
import {type ISavedCart} from '../../schema';
import {
	SavedCartListItems,
	type SavedCartListItemsProps,
} from '../SavedCartListItems';

const SavedCartListRoot = styled('div', {
	name: 'SavedCartList',
	slot: 'Root',
})(({theme}) => ({
	...theme.mixins.flexFillColumn,
	overflow: 'hidden',
}));

const SavedCartListHeader = styled('div', {
	name: 'SavedCartList',
	slot: 'Header',
})(({theme}) => ({
	flex: '0 0 auto',
	padding: theme.spacing(1, 2),
}));

const SavedCartListFooter = styled('div', {
	name: 'SavedCartList',
	slot: 'Footer',
})(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	height: theme.spacing(7),
	background: theme.palette.custom.background.light,
	flex: '0 0 auto',
	padding: theme.spacing(1),
}));

export type SavedCartListProps = {
	filtered?: boolean;
	filterStr?: string;
	savedCarts: ISavedCart[];
	onFilter?: (filter: string) => void;
	onViewAll?: () => void;
	slotProps?: {
		listItems: Partial<SavedCartListItemsProps>;
	};
};

export const SavedCartListPrimitive = ({
	filtered,
	filterStr,
	savedCarts,
	onFilter,
	onViewAll,
	slotProps,
	...props
}: SavedCartListProps) => {
	return (
		<SavedCartListRoot>
			<SavedCartListHeader>
				{onFilter && (savedCarts.length || filtered) ? (
					<SearchFilter
						fullWidth
						variant="rounded"
						filterStr={filterStr}
						onFilter={onFilter}
					/>
				) : null}
			</SavedCartListHeader>
			{!savedCarts.length ? (
				<EmptyStateFallbackMessage
					icon={filtered ? faSearch : faInbox}
					title={
						filtered ? CartsSavedTitle.NO_RECORDS : CartsSavedTitle.NO_RESULTS
					}
					message={filtered ? undefined : CartsSavedMessage.NO_RESULTS}
				/>
			) : (
				<SavedCartListItems {...slotProps?.listItems} />
			)}
			{onViewAll ? (
				<SavedCartListFooter className="hide-mobile">
					<Button fullWidth variant="text" onClick={() => onViewAll()}>
						{CartsSavedLabel.VIEW_ALL}
					</Button>
				</SavedCartListFooter>
			) : null}
		</SavedCartListRoot>
	);
};

export const SavedCartList = (
	props: Omit<SavedCartListProps, 'savedCarts' | 'onFilter'>,
) => {
	return (
		<SavedCartsProvider filterMode={SharedResourceMode.FAVORITE}>
			{({savedCarts, filtered, filter, setFilter}) => (
				<SavedCartListPrimitive
					{...props}
					filtered={filtered}
					savedCarts={savedCarts}
					filterStr={filter}
					onFilter={setFilter}
					onViewAll={() => appRouter.navigate('cart/saved', true)}
				/>
			)}
		</SavedCartsProvider>
	);
};

export default SavedCartList;
